/**
 * Active Workflows Overview
 * 
 * Shows all active workflow processes with progress tracking.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { workflowAPI, employeesAPI } from '../lib/api';
import { formatLastFirst, getDuplicateNameKeys } from '../lib/nameFormat';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  UserPlus, UserMinus, Play, CheckCircle2, Clock, AlertCircle,
  ChevronRight, Loader2, XCircle, Users, FileText, Calendar,
  MapPin, Baby, ShieldCheck, GraduationCap, Zap,
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try { return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de }); }
  catch { return dateStr; }
};

const WORKFLOW_TYPES = [
  { value: 'onboarding', label: 'On-Boarding', icon: UserPlus, color: 'text-green-600', bg: 'bg-green-100' },
  { value: 'offboarding', label: 'Off-Boarding', icon: UserMinus, color: 'text-orange-600', bg: 'bg-orange-100' },
  { value: 'versetzung', label: 'Versetzung', icon: MapPin, color: 'text-blue-600', bg: 'bg-blue-100' },
  { value: 'probezeit', label: 'Probezeit', icon: Clock, color: 'text-purple-600', bg: 'bg-purple-100' },
  { value: 'mutterschutz', label: 'Mutterschutz', icon: Baby, color: 'text-pink-600', bg: 'bg-pink-100' },
  { value: 'zertifikat', label: 'Zertifikate', icon: ShieldCheck, color: 'text-amber-600', bg: 'bg-amber-100' },
  { value: 'pflichtschulung', label: 'Schulungen', icon: GraduationCap, color: 'text-teal-600', bg: 'bg-teal-100' },
  { value: 'vertragsende', label: 'Vertragsende', icon: FileText, color: 'text-red-600', bg: 'bg-red-100' },
];

const getWorkflowTypeConfig = (type) => WORKFLOW_TYPES.find(w => w.value === type) || WORKFLOW_TYPES[0];

const STATUS_CONFIG = {
  active: { label: 'Aktiv', color: 'bg-blue-500', icon: Play },
  completed: { label: 'Abgeschlossen', color: 'bg-green-500', icon: CheckCircle2 },
  cancelled: { label: 'Abgebrochen', color: 'bg-gray-500', icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} text-white`}>
      <Icon className="h-3 w-3 mr-1" />{config.label}
    </Badge>
  );
};

export default function Workflows() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [starting, setStarting] = useState(false);
  const [checkingTriggers, setCheckingTriggers] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [workflowsRes, templatesRes, employeesRes] = await Promise.all([
        workflowAPI.getWorkflows({ status: activeTab === 'all' ? undefined : activeTab }),
        workflowAPI.getTemplates(),
        employeesAPI.getAll(),
      ]);
      setWorkflows(workflowsRes.data);
      setTemplates(templatesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden');
    } finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredWorkflows = workflows.filter(w => {
    if (typeFilter === 'all') return true;
    return w.workflow_type === typeFilter;
  });

  const handleStartWorkflow = async () => {
    if (!selectedTemplate || !selectedEmployee) { toast.error('Bitte Vorlage und Mitarbeiter auswählen'); return; }
    setStarting(true);
    try {
      const response = await workflowAPI.startWorkflow({
        template_id: selectedTemplate, employee_id: selectedEmployee,
      });
      toast.success('Workflow gestartet');
      setStartDialogOpen(false);
      setSelectedTemplate('');
      setSelectedEmployee('');
      navigate(`/workflows/${response.data.id}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler beim Starten'); }
    finally { setStarting(false); }
  };

  const handleCheckTriggers = async () => {
    setCheckingTriggers(true);
    try {
      const res = await workflowAPI.checkTriggers();
      const data = res.data;
      if (data.started > 0) {
        toast.success(`${data.started} Workflow(s) automatisch gestartet`);
        loadData();
      } else {
        toast.info('Keine Workflows zum Auslösen gefunden');
      }
    } catch (error) { toast.error('Fehler beim Prüfen'); }
    finally { setCheckingTriggers(false); }
  };

  const activeCount = workflows.filter(w => w.status === 'active').length;

  // Group templates by type for the start dialog
  const groupedTemplates = WORKFLOW_TYPES.map(wt => ({
    ...wt,
    templates: templates.filter(t => t.workflow_type === wt.value),
  })).filter(g => g.templates.length > 0);

  // Active stats per type
  const typeStats = WORKFLOW_TYPES.map(wt => ({
    ...wt,
    active: workflows.filter(w => w.workflow_type === wt.value && w.status === 'active').length,
  })).filter(s => s.active > 0);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="workflows-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6" />Workflows
            </h1>
            <p className="text-muted-foreground">Alle Workflow-Prozesse verwalten</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <Button
              variant="outline"
              onClick={handleCheckTriggers}
              disabled={checkingTriggers}
              data-testid="check-triggers-btn"
              className="flex-1 md:flex-none"
            >
              {checkingTriggers ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              Auto-Check
            </Button>
            <Link to="/workflow-templates" className="flex-1 md:flex-none">
              <Button variant="outline" className="w-full md:w-auto"><FileText className="h-4 w-4 mr-2" />Vorlagen</Button>
            </Link>
            <Button
              onClick={() => setStartDialogOpen(true)}
              data-testid="start-workflow-btn"
              className="flex-1 md:flex-none"
            >
              <Play className="h-4 w-4 mr-2" />Workflow starten
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {WORKFLOW_TYPES.map(wt => {
            const Icon = wt.icon;
            const count = workflows.filter(w => w.workflow_type === wt.value && w.status === 'active').length;
            return (
              <Card key={wt.value} className={`cursor-pointer transition-all ${typeFilter === wt.value ? 'ring-2 ring-primary' : 'hover:border-primary/30'}`}
                onClick={() => setTypeFilter(typeFilter === wt.value ? 'all' : wt.value)} data-testid={`stat-${wt.value}`}>
                <CardContent className="p-3 text-center">
                  <div className={`inline-flex p-2 rounded-lg ${wt.bg} mb-1`}>
                    <Icon className={`h-4 w-4 ${wt.color}`} />
                  </div>
                  <p className="text-xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground truncate">{wt.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
            <TabsList>
              <TabsTrigger value="active">Aktiv ({activeCount})</TabsTrigger>
              <TabsTrigger value="completed">Abgeschlossen</TabsTrigger>
              <TabsTrigger value="all">Alle</TabsTrigger>
            </TabsList>
          </Tabs>
          {typeFilter !== 'all' && (
            <Button variant="outline" size="sm" onClick={() => setTypeFilter('all')}>
              Filter: {WORKFLOW_TYPES.find(w => w.value === typeFilter)?.label} <XCircle className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>

        {/* Workflow List */}
        {loading ? (
          <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
        ) : filteredWorkflows.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h2 className="text-lg font-semibold mb-2">Keine Workflows</h2>
              <p className="text-muted-foreground mb-4">
                {activeTab === 'active' ? 'Keine aktiven Workflow-Prozesse.' : 'Keine Workflows in dieser Kategorie.'}
              </p>
              <Button onClick={() => setStartDialogOpen(true)}>
                <Play className="h-4 w-4 mr-2" />Workflow starten
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredWorkflows.map((workflow) => {
              const completedTasks = workflow.tasks?.filter(t => t.status === 'completed' || t.status === 'skipped').length || 0;
              const totalTasks = workflow.tasks?.length || 0;
              const overdueTasks = workflow.tasks?.filter(t => {
                if (t.status !== 'pending' || !t.deadline_date) return false;
                return new Date(t.deadline_date) < new Date();
              }).length || 0;
              const typeConfig = getWorkflowTypeConfig(workflow.workflow_type);
              const TypeIcon = typeConfig.icon;

              return (
                <Card key={workflow.id} className="border-border/60 hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/workflows/${workflow.id}`)} data-testid={`workflow-${workflow.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${typeConfig.bg}`}>
                          <TypeIcon className={`h-6 w-6 ${typeConfig.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold">{workflow.employee_name}</h3>
                            <Badge variant="outline" className="text-xs">{typeConfig.label}</Badge>
                            <StatusBadge status={workflow.status} />
                          </div>
                          <p className="text-sm text-muted-foreground">{workflow.template_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Gestartet: {formatDate(workflow.started_at)}
                            </span>
                            {workflow.started_by_name?.startsWith('Automatisch') && (
                              <Badge variant="secondary" className="text-xs"><Zap className="h-3 w-3 mr-1" />Auto</Badge>
                            )}
                            {workflow.completed_at && (
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Abgeschlossen: {formatDate(workflow.completed_at)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right min-w-[120px]">
                          <div className="flex items-center justify-end gap-2 mb-1">
                            <span className="text-sm font-medium">{workflow.progress}%</span>
                            <span className="text-xs text-muted-foreground">({completedTasks}/{totalTasks})</span>
                          </div>
                          <Progress value={workflow.progress} className="h-2 w-[120px]" />
                          {overdueTasks > 0 && (
                            <p className="text-xs text-red-500 mt-1 flex items-center justify-end gap-1">
                              <AlertCircle className="h-3 w-3" />{overdueTasks} überfällig
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Start Workflow Dialog */}
        <Dialog open={startDialogOpen} onOpenChange={setStartDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Workflow starten</DialogTitle>
              <DialogDescription>Starten Sie einen Workflow-Prozess für einen Mitarbeiter.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Vorlage *</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="template-select"><SelectValue placeholder="Vorlage wählen" /></SelectTrigger>
                  <SelectContent>
                    {groupedTemplates.map(group => {
                      const Icon = group.icon;
                      return (
                        <React.Fragment key={group.value}>
                          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground flex items-center gap-1 mt-1">
                            <Icon className={`h-3 w-3 ${group.color}`} />{group.label}
                          </div>
                          {group.templates.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mitarbeiter *</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger data-testid="employee-select"><SelectValue placeholder="Mitarbeiter wählen" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        {formatLastFirst(e)} ({e.employee_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStartDialogOpen(false)}>Abbrechen</Button>
              <Button onClick={handleStartWorkflow} disabled={starting || !selectedTemplate || !selectedEmployee} data-testid="confirm-start-workflow">
                {starting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                Starten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
